import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { MrbBaseViewStore } from "mrb/core";
import { MrbTableViewStore } from "mrb/components";
import { MrbSelectStore } from "mrb/components/select";
import { DropdownItemsResult } from "mrb/components/select/models";
import { mrbDefaultFormats } from "mrb/common/formatting";
import { dateFormatter } from "mrb/common/formatting";
import { applicationDefaults } from "common/constants";
import { ProducerPreviewReportPeriodCell, ProducerPreviewReportPlaybackTimeCell } from "common/components/producer";
import { numberFormatter } from "common/utils";
import { MrbQueryUtility } from "mrb/core/filter";
import { isNil } from "lodash";
import moment from "moment";

class ProducerRevenueViewStore extends MrbBaseViewStore {
    range = null;
    @observable.ref summaryData = null;
    @observable reportsGridStore = null;
    @observable revenueGridStore = null;
    @observable balanceGridStore = null;
    reportsQueryUtility = null;
    revenueQueryUtility = null;
    balanceQueryUtility = null;
    @observable producers = null;
    @observable selectedProducerId = null;
    @observable payoutsEnabled = false;
    @observable isAutomaticPayoutEnabled = false;
    @observable isAutomaticPayoutUpdateInProgress = false;
    @observable.ref onboardingStatus = null;
    @observable stripeAccountLink;
    @observable isCreateStripeAccountButtonDisabled = false;
    @observable isRestartStripeConnectInProgress = false;
    @observable selectedCountry = null;
    @observable.ref supportedCountries = [];

    producerId = null;

    @computed get isStripeAccountConnected() {
        if (this.summaryData) {
            return !isNil(this.summaryData.stripeAccountId);
        }
        return false;
    }

    @computed get isCountrySelectionValid() {
        return !!this.selectedCountry && this.supportedCountries.some((c) => c.code === this.selectedCountry);
    }

    constructor(rootStore, { routeStore }) {
        super(rootStore);
        makeObservable(this);
        this.routeStore = routeStore;

        this.producerId = this.rootStore.routerStore.routerState.queryParams.producerId;

        this.countrySelectStore = new MrbSelectStore({
            isSearchable: false,
            virtualized: false,
            initFetch: false,
            isClearable: false,
            textKey: "name",
            valueKey: "code",
            actions: {
                onChange: (option) => {
                    this.setSelectedCountry(option ? option.value : null);
                },
            },
        });
        this.countrySelectStore.originalItems = new DropdownItemsResult([], 1, 0, 0);

        this.revenueQueryUtility = new MrbQueryUtility(
            this.rootStore,
            async (filter) => {
                this.revenueFetchResources(filter);
            },
            {
                orderBy: "dateCreated",
                orderDirection: "desc",
                ignoreQueryParams: true,
                disableUpdateQueryParams: true,
            }
        );

        this.balanceQueryUtility = new MrbQueryUtility(
            this.rootStore,
            async (filter) => {
                this.balanceFetchResources(filter);
            },
            {
                orderBy: "dateCreated",
                orderDirection: "desc",
                ignoreQueryParams: true,
                disableUpdateQueryParams: true,
            }
        );

        this.reportsQueryUtility = new MrbQueryUtility(
            this.rootStore,
            async (filter) => {
                this.reportsFetchResources(filter);
            },
            {
                orderBy: "monthOfYear",
                orderDirection: "desc",
                ignoreQueryParams: true,
                disableUpdateQueryParams: true,
            }
        );
        this.revenueFetchResources = this.revenueFetchResources.bind(this);
        this.balanceFetchResources = this.balanceFetchResources.bind(this);

        const from = moment.utc().subtract(1, "year").startOf("month").format(applicationDefaults.formats.isoDate);
        const to = moment.utc().subtract(1, "month").startOf("month").format(applicationDefaults.formats.isoDate);
        this.range = {
            from: from,
            to: to,
        };

        this.setReportsGridStore(
            new MrbTableViewStore(this.rootStore, this.reportsQueryUtility, {
                onRowClick: (node) => {
                    const from = dateFormatter.format(node.data.monthOfYear, {
                        format: applicationDefaults.formats.isoDate,
                        timeZone: "utc",
                    });
                    let to;
                    if (moment(from).isSame(moment(), "month")) {
                        to = moment().format(applicationDefaults.formats.isoDate);
                    } else {
                        to = moment(from).endOf("month").format(applicationDefaults.formats.isoDate);
                    }
                    this.rootStore.routerStore.goTo(
                        "master.application.producer.report",
                        {
                            id: this.selectedProducerId,
                        },
                        {
                            from: from,
                            to: to,
                        }
                    );
                },
                columns: [
                    {
                        key: "monthOfYear",
                        title: "Period",
                        sortable: true,
                        cell: ProducerPreviewReportPeriodCell,
                    },
                    {
                        key: "totalProducerPlaybacks",
                        title: "Playback Time",
                        sortable: true,
                        cell: ProducerPreviewReportPlaybackTimeCell,
                    },
                    {
                        key: "totalProducerPlaybacks",
                        title: "Playback Points",
                        format: (value) => numberFormatter.format(value),
                    },
                    {
                        key: "totalProducerPayment",
                        title: "Revenue",
                        sortable: true,
                        format: {
                            type: mrbDefaultFormats.currency,
                            currency: "USD",
                        },
                    },
                ],
            })
        );

        this.setRevenueGridStore(
            new MrbTableViewStore(this.rootStore, this.revenueQueryUtility, {
                columns: [
                    {
                        key: "dateCreated",
                        title: "Date",
                        sortable: true,
                        format: (value) =>
                            dateFormatter.format(value, {
                                format: applicationDefaults.formats.dateTime,
                            }),
                    },
                    {
                        key: "stripeTransactionId",
                        title: "Stripe Transaction Id",
                    },
                    {
                        key: "amount",
                        title: "Amount",
                        sortable: true,
                        format: {
                            type: mrbDefaultFormats.currency,
                            currency: "USD",
                        },
                    },
                ],
            })
        );

        this.setBalanceGridStore(
            new MrbTableViewStore(this.rootStore, this.balanceQueryUtility, {
                columns: [
                    {
                        key: "dateCreated",
                        title: "Date",
                        sortable: true,
                        format: (value) =>
                            dateFormatter.format(value, {
                                format: applicationDefaults.formats.dateTime,
                            }),
                    },
                    {
                        key: "notes",
                        title: "Notes",
                    },
                    {
                        key: "amount",
                        title: "Amount",
                        sortable: true,
                        format: {
                            type: mrbDefaultFormats.currency,
                            currency: "USD",
                        },

                        cellClass: (item) => {
                            if (item.value > 0) {
                                return "u-type--color--success";
                            } else if (item.value < 0) {
                                return "u-type--color--warning";
                            }
                        },
                    },
                    {
                        key: "producer.name",
                        title: "Producer",
                    },
                ],
            })
        );
    }

    onFetchError(err) {
        this.rootStore.notificationStore.error(
            "An unexpected error occurred while trying to load data. Please contact the support team.",
            err
        );
    }

    async reportsFetchResources(filter) {
        this.reportsGridStore.suspend();
        try {
            filter.producerId = this.selectedProducerId;
            const response = await this.fetchReportsData(filter);
            runInAction(() => {
                this.reportsGridStore.setData(response);
                this.reportsGridStore.resume();
            });
        } catch (err) {
            this.reportsGridStore.resume();
            this.onFetchError(err);
        }
    }

    async revenueFetchResources(filter) {
        this.revenueGridStore.suspend();
        try {
            const response = await this.fetchRevenueData(filter);
            runInAction(() => {
                this.revenueGridStore.setData(response);
                this.revenueGridStore.resume();
            });
        } catch (err) {
            this.revenueGridStore.resume();
            this.onFetchError(err);
        }
    }

    async balanceFetchResources(filter) {
        this.balanceGridStore.suspend();
        try {
            const response = await this.fetchBalanceData(filter);
            runInAction(() => {
                this.balanceGridStore.setData(response);
                this.balanceGridStore.resume();
            });
        } catch (err) {
            this.balanceGridStore.resume();
            this.onFetchError(err);
        }
    }

    async fetchReportsData(filter) {
        try {
            return this.routeStore.findProducerReports(filter);
        } catch (error) {
            this.onFetchError(error);
        }
    }

    async fetchRevenueData(filter) {
        try {
            return await this.routeStore.getProducerAccountRevenueTransaction(filter);
        } catch (error) {
            this.onFetchError(error);
        }
    }

    async fetchBalanceData(filter) {
        try {
            return await this.routeStore.getProducerAccountBalance(filter);
        } catch (error) {
            this.onFetchError(error);
        }
    }

    async onInit() {
        await Promise.all([
            this.fetchProducers(),
            this.fetchAccountSummaryData(),
            this.fetchSupportedCountries(),
            this.revenueQueryUtility.initialize(),
            this.balanceQueryUtility.initialize(),
        ]);

        this.reaction(
            () => this.rootStore.producerPreviewViewStore?.selectedProducerId,
            (newId) => { if (newId) this.selectProducer(newId); }
        );
    }

    async fetchSupportedCountries() {
        try {
            const codes = await this.routeStore.getStripeConnectSupportedCountries();
            const regionNames =
                typeof Intl !== "undefined" && Intl.DisplayNames
                    ? new Intl.DisplayNames(["en"], { type: "region" })
                    : null;
            const countries = (codes || [])
                .map((code) => ({
                    code,
                    name: regionNames ? regionNames.of(code) || code : code,
                }))
                .sort((a, b) => a.name.localeCompare(b.name));
            this.setSupportedCountries(countries);
            this.countrySelectStore.originalItems = new DropdownItemsResult(
                countries,
                1,
                countries.length,
                countries.length
            );
        } catch (err) {
            this.setSupportedCountries([]);
        }
    }

    @action.bound
    setSupportedCountries(countries) {
        this.supportedCountries = countries;
    }

    @action.bound
    setSelectedCountry(code) {
        this.selectedCountry = code;
    }

    @action.bound
    setStripeAccountOnboardingStatus(data) {
        this.onboardingStatus = data;
        // payoutsEnabled is the single gate: it's only true once Stripe has fully verified the
        // account and it can receive payouts. Until then (unfinished, restricted, or pending
        // verification) the producer is sent back to Stripe to continue.
        this.setPayoutsEnabled(!!(data && data.payoutsEnabled));
    }

    @action.bound
    setPayoutsEnabled(state) {
        this.payoutsEnabled = state;
    }

    @action.bound
    setStripeAccountLink(data) {
        this.stripeAccountLink = data;
    }

    @action.bound
    setSummaryData(data) {
        this.summaryData = data;
        this.setIsAutomaticPayoutEnabled(!!(data && data.isAutomaticPayoutEnabled));
    }

    @action.bound
    setIsAutomaticPayoutEnabled(state) {
        this.isAutomaticPayoutEnabled = state;
    }

    @action.bound
    setIsAutomaticPayoutUpdateInProgress(state) {
        this.isAutomaticPayoutUpdateInProgress = state;
    }

    @action.bound
    async toggleAutomaticPayout() {
        const nextState = !this.isAutomaticPayoutEnabled;
        // Optimistically reflect the new state, revert if the request fails.
        this.setIsAutomaticPayoutEnabled(nextState);
        try {
            this.setIsAutomaticPayoutUpdateInProgress(true);
            await this.routeStore.setAutomaticPayoutEnabled(nextState);
        } catch (err) {
            this.setIsAutomaticPayoutEnabled(!nextState);
            this.rootStore.notificationStore.error(
                "An unexpected error occurred while trying to update automatic payouts. Please contact the support team.",
                err
            );
        } finally {
            this.setIsAutomaticPayoutUpdateInProgress(false);
        }
    }

    @action.bound
    setReportsGridStore(reportsGridStore) {
        this.reportsGridStore = reportsGridStore;
    }

    @action.bound
    setRevenueGridStore(revenueGridStore) {
        this.revenueGridStore = revenueGridStore;
    }

    @action.bound
    setBalanceGridStore(balanceGridStore) {
        this.balanceGridStore = balanceGridStore;
    }

    @action.bound
    setProducers(producers) {
        this.producers = producers;
    }

    @action.bound
    setIsCreateStripeAccountButtonDisabled(state) {
        this.isCreateStripeAccountButtonDisabled = state;
    }

    @action.bound
    setIsRestartStripeConnectInProgress(state) {
        this.isRestartStripeConnectInProgress = state;
    }

    @action.bound
    async selectProducer(producerId) {
        this.selectedProducerId = producerId;
        this.rootStore.producerPreviewViewStore?.setSelectedProducerId(producerId);
        await this.reportsQueryUtility.initialize();
    }

    @action.bound
    async createStripeAccount() {
        try {
            this.setIsCreateStripeAccountButtonDisabled(true);
            const resource = {
                country: this.selectedCountry,
                returnUrl: this.generateCurrentUrl(),
                refreshUrl: this.generateCurrentUrl(),
            };
            const data = await this.routeStore.createStripeConnectLink(resource);
            this.setStripeAccountLink(data);

            await this.fetchAccountSummaryData();
        } catch (err) {
            this.rootStore.notificationStore.error(
                "An unexpected error occurred while trying to load data. Please contact the support team.",
                err
            );
        } finally {
            this.setIsCreateStripeAccountButtonDisabled(false);
        }
    }

    @action.bound
    restartStripeConnect() {
        this.rootStore.confirmDialogStore.showConfirm({
            message: "Restart account setup?",
            description:
                "This removes your current Stripe Connect account and starts the process over. You'll need to complete onboarding again.",
            yesLabel: "Restart",
            onConfirm: async () => {
                try {
                    this.setIsRestartStripeConnectInProgress(true);
                    await this.routeStore.restartStripeConnect();
                    this.setStripeAccountLink(null);
                    this.setStripeAccountOnboardingStatus(null);
                    await this.fetchAccountSummaryData();
                } catch (err) {
                    this.rootStore.notificationStore.error(
                        "An unexpected error occurred while trying to restart the Stripe Connect process. Please contact the support team.",
                        err
                    );
                } finally {
                    this.setIsRestartStripeConnectInProgress(false);
                }
            },
        });
    }

    async fetchAccountSummaryData() {
        try {
            const data = await this.routeStore.getProducerAccountSummary();
            this.setSummaryData(data);
            if (this.isStripeAccountConnected) {
                await this.fetchStripeAccountOnboardingStatus();

                // Fetch a "Continue on Stripe" link whenever payouts aren't enabled yet
                // (unfinished, restricted, or pending verification).
                if (isNil(this.stripeAccountLink) && !this.payoutsEnabled) {
                    await this.fetchStripeAccountLink();
                }
            }
        } catch (err) {
            this.rootStore.notificationStore.error(
                "An unexpected error occurred while trying to load data. Please contact the support team.",
                err
            );
        }
    }

    async fetchStripeAccountOnboardingStatus() {
        try {
            const data = await this.routeStore.getStripeConnectOnboardingStatus();
            this.setStripeAccountOnboardingStatus(data);
        } catch (err) {
            this.rootStore.notificationStore.error(
                "An unexpected error occurred while trying to load data. Please contact the support team.",
                err
            );
        }
    }

    async fetchStripeAccountLink() {
        try {
            const resource = { returnUrl: this.generateCurrentUrl(), refreshUrl: this.generateCurrentUrl() };
            const data = await this.routeStore.createStripeConnectLink(resource);
            this.setStripeAccountLink(data);
        } catch (err) {
            this.rootStore.notificationStore.error(
                "An unexpected error occurred while trying to load data. Please contact the support team.",
                err
            );
        }
    }

    async fetchProducers() {
        try {
            const producers = await this.routeStore.getProducers();
            this.setProducers(producers);
            const parentSelectedId = this.rootStore.producerPreviewViewStore?.selectedProducerId;
            if (!isNil(this.producerId)) {
                this.selectProducer(this.producerId);
            } else if (!isNil(parentSelectedId) && producers.some((p) => p.id === parentSelectedId)) {
                this.selectProducer(parentSelectedId);
            } else {
                this.selectProducer(producers[0].id);
            }
        } catch (err) {
            this.rootStore.notificationStore.error(
                "An unexpected error occurred while trying to load data. Please contact the support team.",
                err
            );
        }
    }

    generateCurrentUrl() {
        return `${window.location.origin}/producer/revenue`;
    }
}

export default ProducerRevenueViewStore;
